import { createFileRoute, redirect } from "@tanstack/react-router";
import { marketingHead } from "@/lib/marketing/seo";
import { breadcrumbs, employeeService } from "@/lib/marketing/ppl-schema";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { MarketingCta } from "@/components/marketing/site/marketing-cta";
import { EMPLOYEE_BY_SLUG, isEmployeeSlug, tracesFor, type Employee } from "@alm/domain/marketing";
import { FilmSection } from "@/components/marketing/site/film-section";
import { EMPLOYEE_FILMS } from "@/lib/marketing/employee-films";
import { FILMS_ON, filmOgVideo, filmVideoObject } from "@/lib/marketing/film";
import { RoleHero } from "@/routes/v2/-components/role-hero";
import { RoleOwns } from "@/routes/v2/-components/role-owns";
import { RoleTurn } from "@/routes/v2/-components/role-turn";
import { RoleTraces } from "@/routes/v2/-components/role-traces";
import { RoleDemos } from "@/routes/v2/-components/role-demos";
import { RoleWorksIn } from "@/routes/v2/-components/role-works-in";
import { RoleRecord } from "@/routes/v2/-components/role-record";
import { PROOF, recordFor } from "@/routes/v2/-lib/workforce-role-data";

/** One component for all nine workforce pages, keyed by slug. An unknown slug returns to the
 * roster, not a 404 a search engine would index. The superagent has no trace, so "at work" renders
 * only with content and Aria gets the turn instead. */
function PeopleWorkforceRole() {
  /* `beforeLoad` has already turned an unknown desk into a real redirect, so
     by here the slug is one of the nine. The check that remains is the type
     narrowing, not a second guard. */
  const { slug } = Route.useParams();
  if (!isEmployeeSlug(slug)) return null;

  const employee: Employee = EMPLOYEE_BY_SLUG[slug];
  const traces = tracesFor(slug);
  const caps = recordFor(slug);
  const proof = PROOF[slug];
  const film = EMPLOYEE_FILMS[slug];
  const isSuperagent = employee.kind === "superagent";
  /* Only some desks have a capability record.ts marks as a differentiator, and
     the lede promises a second paragraph the reader can look for. A desk of
     table stakes must not promise one. */
  const hasDepth = caps.some((capability) => capability.differentiator);

  return (
    <MarketingShell>
      <RoleHero slug={slug} employee={employee} isSuperagent={isSuperagent} />

      {film && <FilmSection id={`${slug}-film`} trigger={`workforce-${slug}-film`} {...film} />}

      <RoleOwns employee={employee} />

      <RoleTurn isSuperagent={isSuperagent} />

      <RoleTraces employee={employee} traces={traces} />

      <RoleDemos slug={slug} />

      <RoleWorksIn
        employee={employee}
        caps={caps}
        proof={proof}
        isSuperagent={isSuperagent}
        hasDepth={hasDepth}
      />

      <RoleRecord employee={employee} />

      <MarketingCta
        title={isSuperagent ? "See Aria in a demo" : `Put ${employee.name} to work`}
        subtitle={
          isSuperagent
            ? "Your agent inside the operating system, works as you, with your access, and the button stays yours"
            : "One AI employee or a full workforce, priced the same way"
        }
        secondaryLabel="The workforce"
        secondaryHref="/workforce"
      />
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/workforce/$slug")({
  component: PeopleWorkforceRole,
  /* An unknown desk is the roster, as a real redirect rather than a page that
     redirects itself after it has been served. */
  beforeLoad: ({ params }) => {
    if (!isEmployeeSlug(params.slug)) throw redirect({ to: "/v2/workforce", replace: true });
  },
  head: ({ params }) => {
    const employee = isEmployeeSlug(params.slug) ? EMPLOYEE_BY_SLUG[params.slug] : undefined;
    const film = FILMS_ON && employee ? EMPLOYEE_FILMS[employee.slug]?.film : undefined;
    // The eight desks carry their own suffixed <title> in workforce.ts.
    return marketingHead({
      title: employee?.title ?? "PEOPLE AI Workforce",
      description: employee?.description,
      path: `/v2/workforce/${params.slug}`,
      imageAlt: "PEOPLE AI Workforce. The operating system for assisted living communities",
      video: film ? filmOgVideo(film) : undefined,
      schema: employee
        ? [
            employeeService(employee),
            breadcrumbs(
              { name: "AI Workforce", path: "/workforce" },
              { name: employee.name, path: `/workforce/${employee.slug}` },
            ),
            ...(film
              ? [filmVideoObject(film, `/workforce/${employee.slug}#${employee.slug}-film`)]
              : []),
          ]
        : [],
    });
  },
});
